import React, { useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import useReport from './useReport';
import { formatAmount } from '../../utils/formatAmount';

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const columns = [
  { key: 'contact', label: 'Contact', numeric: false },
  { key: 'lastOrderDate', label: 'Last Order Date', numeric: false },
  { key: 'registerDate', label: 'Register Date', numeric: false },
  { key: 'orderCount', label: 'Order Count', numeric: true },
  { key: 'total', label: 'Total (THB)', numeric: true },
  { key: 'lastMonthTotal', label: 'Last Month Total (THB)', numeric: true },
];

const headerStyle = { color: 'white', fontWeight: 'bold' };
const headerCellSx = {
  color: 'white !important',
  '& .MuiTableSortLabel-icon': { color: 'white !important' },
};

const CompanyCustomerSummaryReport = () => {
  const { reportData, isLoading, error, getCompanyCustomerSummaryReport } = useReport();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'lastMonthTotal', direction: 'desc' });

  useEffect(() => {
    getCompanyCustomerSummaryReport({ limit: 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customers = reportData?.data?.customers || [];
  const summary = reportData?.data?.summary || null;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchText(searchText), 250);
    return () => clearTimeout(t);
  }, [searchText]);

  const filteredAndSorted = useMemo(() => {
    const keyword = debouncedSearchText.trim().toLowerCase();
    const filtered = keyword
      ? customers.filter((row) => {
          return (
            (row.contact && row.contact.toLowerCase().includes(keyword)) ||
            (row.smileName && row.smileName.toLowerCase().includes(keyword))
          );
        })
      : customers;

    const { key, direction } = sortConfig;
    if (!key) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (key === 'lastOrderDate' || key === 'registerDate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === 'string' || typeof bVal === 'string') {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [customers, debouncedSearchText, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      const numericDefaults = ['orderCount', 'lastMonthTotal', 'total', 'lastOrderDate', 'registerDate'];
      return { key, direction: numericDefaults.includes(key) ? 'desc' : 'asc' };
    });
  };

  const handleOpenProfile = (smileName) => {
    if (!smileName) return;
    const url = `/customer-profile?smileName=${encodeURIComponent(smileName)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6 w-full">
      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} className="mb-6">
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#4e46e5', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">Total Customers</Typography>
                <Typography variant="h4">{summary.totalCustomers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#0f766e', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">Total Orders</Typography>
                <Typography variant="h4">{formatAmount(summary.totalOrderCount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#d97706', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">Last Month Total</Typography>
                <Typography variant="h4">{formatAmount(summary.totalLastMonthAmount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card style={{ backgroundColor: '#059669', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">Total Amount</Typography>
                <Typography variant="h4">{formatAmount(summary.totalAmount)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search + Refresh */}
      <Card>
        <CardContent>
          <Box className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <TextField
              label="Search (contact / smile name)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              style={{ minWidth: 280 }}
            />
            <Button
              variant="contained"
              onClick={() => getCompanyCustomerSummaryReport({ limit: 100 })}
              disabled={isLoading}
              style={{ backgroundColor: '#4e46e5' }}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
            {error && (
              <Typography color="error" variant="body2">
                Failed to load report.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && customers.length === 0 ? (
        <div className="flex justify-center py-10">
          <CircularProgress />
        </div>
      ) : filteredAndSorted.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table aria-label="company customer summary table">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.numeric ? 'right' : 'left'}
                      style={{ ...headerStyle, backgroundColor: '#4e46e5' }}
                    >
                      <TableSortLabel
                        active={sortConfig.key === col.key}
                        direction={sortConfig.key === col.key ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(col.key)}
                        sx={headerCellSx}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSorted.map((row, index) => (
                  <TableRow
                    key={row.customerId || index}
                    hover
                    style={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white' }}
                  >
                    <TableCell
                      onClick={() => handleOpenProfile(row.smileName)}
                      style={{
                        fontWeight: 'bold',
                        color: '#4e46e5',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      {row.contact || '-'}
                    </TableCell>
                    <TableCell>{formatDate(row.lastOrderDate)}</TableCell>
                    <TableCell>{formatDate(row.registerDate)}</TableCell>
                    <TableCell align="right">{formatAmount(row.orderCount)}</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold', color: '#059669' }}>
                      {formatAmount(row.total)}
                    </TableCell>
                    <TableCell align="right">{formatAmount(row.lastMonthTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        !isLoading && (
          <div className="text-center py-8 text-gray-500">
            <Typography variant="h6">No company customer data found.</Typography>
          </div>
        )
      )}
    </div>
  );
};

export default CompanyCustomerSummaryReport;
